/**
 * Badge — soft-tinted rounded chip, mono uppercase.
 * A light tint of the tone's colour behind dark text of the same colour, no
 * border. The one rounded element in an otherwise sharp-cornered UI, so a
 * status flag (Registr. / Skladový / Vyplaceno) reads as a label and never as
 * a control. Marker is the dot-led label sibling; Pill is the neutral one.
 */

export type BadgeTone = 'accent' | 'ok' | 'warn' | 'neutral' | 'danger';

// bg = the tone's `-soft` tint, text = its AA label colour (both flip with the theme).
const TONE_CLASSES: Record<BadgeTone, string> = {
  accent: 'bg-accent-soft text-accent-text',
  ok: 'bg-ok-soft text-ok',
  warn: 'bg-warn-soft text-warn',
  neutral: 'bg-neutral-soft text-ink-2',
  danger: 'bg-red-soft text-red-text',
};

export type BadgeSize = 'md' | 'sm';

// md is the default; sm is for dense table cells (stav columns) where the
// full-size chip crowds a narrow column.
const SIZE_CLASSES: Record<BadgeSize, string> = {
  md: 'gap-1.5 px-2.5 py-[3px] text-[10.5px] tracking-[0.05em]',
  sm: 'gap-1 px-2 py-[1px] text-[9.5px] tracking-[0.04em]',
};

interface BadgeProps {
  children: React.ReactNode;
  tone?: BadgeTone;
  size?: BadgeSize;
  className?: string;
}

export function Badge({ children, tone = 'neutral', size = 'md', className = '' }: BadgeProps) {
  return (
    <span
      className={
        'inline-flex items-center rounded-full font-mono font-semibold uppercase ' +
        SIZE_CLASSES[size] +
        ' ' +
        TONE_CLASSES[tone] +
        ' ' +
        className
      }
    >
      {children}
    </span>
  );
}
